import {useState, useEffect, useRef, useCallback} from "react";
import {  useAddLegalDocument,} from "../../../../hooks/useLegalQueries.js";
import { BackIcon, DOC_ICONS, docMeta, EyeIcon, DownloadIcon } from "../../../../Component/Workshop/UI/icons";
import { Avatar ,docBadge,formatDate, expiryLabel,ulipBadgeVariant } from "../../../../Component/Workshop/UI/index";
import {
  DOCUMENT_FIELDS,
  DRIVER_DOC_FIELDS,
  HELPER_DOC_FIELDS,
  FIR_DOC_FIELD,
  LR_DOC_FIELD,
  ACCIDENT_IMAGE_FIELDS,
} from "../../../../Pages/Customers/WorkshopDashboard/workshopConstants";

export function LegalDocPreviewModal({ doc, onClose, onApprove, onReject, isKAM }) {
  if (!doc) return null;

  const meta = docMeta(doc.type);
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.fileUrl || "");

  const expiryColor = (() => {
    if (!doc.ulipExpiryDate) return "var(--wd-t2)";
    const diff = (new Date(doc.ulipExpiryDate) - new Date()) / (1000 * 60 * 60 * 24);
    if (diff < 0) return "var(--wd-red)";
    if (diff < 30) return "var(--wd-orange)";
    return "var(--wd-green)";
  })();

  const expLabel = expiryLabel(doc.ulipExpiryDate);
  const isPending = ["PENDING", null, undefined].includes(doc.status);

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: "fixed", inset: 0, zIndex: 1300, background: "rgba(0,0,0,.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div style={{ width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto", borderRadius: 14, background: "var(--wd-surface,#111827)", border: "1px solid var(--wd-border)" }}>
        {/* header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--wd-border)", position: "sticky", top: 0, background: "var(--wd-surface,#111827)" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--wd-t1)" }}>Document Verification</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "var(--wd-t3)" }}>✕</button>
        </div>

        <div style={{ padding: 20 }}>
          {/* preview */}
          <div style={{ height: 200, borderRadius: 10, overflow: "hidden", background: "var(--wd-hover)", border: "1px solid var(--wd-border)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 4 }}>
            {doc.fileUrl && isImage ? (
              <img src={doc.fileUrl} alt={doc.type} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            ) : doc.fileUrl ? (
              <iframe src={doc.fileUrl} title={doc.type} style={{ width: "100%", height: "100%", border: "none" }} />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 40 }}>{meta.icon}</span>
                <span style={{ fontSize: 12, color: "var(--wd-t3)" }}>No file available</span>
              </div>
            )}
          </div>

          {/* doc header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0 8px" }}>
            <div style={{ width: 40, height: 40, borderRadius: 9, flexShrink: 0, background: `${meta.color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
              {meta.icon}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: "var(--wd-t1)" }}>{(doc.type || "").replace(/_/g, " ")}</div>
              <div style={{ fontSize: 12, color: "var(--wd-t3)", marginTop: 2 }}>Uploaded by {doc.uploadedBy || "—"}</div>
            </div>
            <div style={{ marginLeft: "auto" }}>
              <Badge variant={docBadge(doc.status)}>{doc.status || "PENDING"}</Badge>
            </div>
          </div>

          <div style={{ height: 1, background: "var(--wd-border)", margin: "4px 0 16px" }} />

          {/* ULIP fields — populated only when the legal backend supplies them (see caveat) */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em" }}>ULIP Verified</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: doc.ulipStatus ? (["VALID", "EXPIRING_SOON"].includes(doc.ulipStatus) ? "var(--wd-green)" : "var(--wd-red)") : "var(--wd-t3)" }}>
                {doc.ulipStatus ? (["VALID", "EXPIRING_SOON"].includes(doc.ulipStatus) ? "Yes" : "No") : "Not verified yet"}
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em" }}>ULIP Status</span>
              {doc.ulipStatus ? (
                <Badge variant={ulipBadgeVariant(doc.ulipStatus)}>{doc.ulipStatus.replace(/_/g, " ")}</Badge>
              ) : (
                <span style={{ fontSize: 13, color: "var(--wd-t3)", fontStyle: "italic" }}>Not verified yet</span>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em" }}>Expiry Date</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: expiryColor }}>{doc.ulipExpiryDate ? formatDate(doc.ulipExpiryDate) : "—"}</span>
                {expLabel && (
                  <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 6, background: `${expLabel.color}18`, color: expLabel.color, border: `1px solid ${expLabel.color}33` }}>
                    {expLabel.text}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Approve / Reject */}
          {isPending && !isKAM && (
            <>
              <div style={{ height: 1, background: "var(--wd-border)", margin: "16px 0" }} />
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button
                  className="wd-btn wd-btn-sm"
                  style={{ background: "rgba(34,197,94,.12)", color: "var(--wd-green)", border: "1px solid rgba(34,197,94,.25)", fontWeight: 600 }}
                  onClick={() => { onApprove?.(doc); onClose(); }}
                >
                  ✓ Approve
                </button>
                <button className="wd-btn wd-btn-danger wd-btn-sm" onClick={() => { onReject?.(doc); onClose(); }}>
                  ✗ Reject
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── File drop zone — identical to WorkshopTabHelpers' DocFileZone ── */
export function DocFileZone({ value, onChange, disabled }) {
  const ref = useRef(null);
  const filled = !!value;
  const fmtSize = (b) => (b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);
  return (
    <div
      onClick={() => !disabled && ref.current?.click()}
      style={{
        display: "flex", alignItems: "center", gap: 7,
        minHeight: 38, padding: "6px 9px", borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer",
        border: filled ? "1px solid rgba(99,102,241,.4)" : "1px solid var(--wd-border)",
        background: filled ? "rgba(99,102,241,.07)" : "rgba(255,255,255,.02)",
        transition: "border-color .12s, background .12s", opacity: disabled ? 0.5 : 1, position: "relative",
      }}
    >
      <input
        ref={ref} type="file" accept="image/*,application/pdf" disabled={disabled}
        onChange={(e) => onChange(e.target.files[0] || null)}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
      />
      <div style={{
        width: 24, height: 24, borderRadius: 6, flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11,
        background: filled ? "rgba(99,102,241,.15)" : "rgba(148,163,184,.07)",
        border: filled ? "1px solid rgba(99,102,241,.25)" : "1px solid var(--wd-border)",
        color: filled ? "#818cf8" : "var(--wd-t3)",
      }}>
        {filled ? "✓" : "↑"}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: filled ? "#a5b4fc" : "var(--wd-t3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {filled ? `${value.name} · ${fmtSize(value.size)}` : "Choose or drop file"}
        </div>
      </div>
      {filled && (
        <button onClick={(e) => { e.stopPropagation(); onChange(null); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--wd-t3)", padding: "2px 4px" }}>✕</button>
      )}
    </div>
  );
}

export function DocSectionHead({ icon, label, count }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 10, borderBottom: "1px solid var(--wd-border)", marginBottom: 12 }}>
      <span style={{ fontSize: 14 }}>{icon}</span>
      <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--wd-t2)" }}>{label}</span>
      {count != null && (
        <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 10, background: "rgba(99,102,241,.1)", color: "#818cf8" }}>
          {count} fields
        </span>
      )}
    </div>
  );
}

export function DocFieldRow({ label, docKey, files, onChange, existingUrl, disabled }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11, fontWeight: 600, color: "var(--wd-t2)" }}>
        <span>{label}</span>
        {existingUrl && (
          <a href={existingUrl} target="_blank" rel="noreferrer" style={{ fontSize: 10, fontWeight: 600, color: "#7dd3fc", padding: "1px 6px", borderRadius: 5, background: "rgba(125,211,252,.07)", border: "1px solid rgba(125,211,252,.15)" }}>
            ↗ View current
          </a>
        )}
      </div>
      <DocFileZone value={files[docKey] || null} onChange={(f) => onChange(docKey, f)} disabled={disabled} />
    </div>
  );
}

/* ── Upload modal — same field set + optional-section toggles as Workshop's UploadDocsModal, uploading via useAddLegalDocument ── */
export function UploadLegalDocsModal({ open, onClose, caseNo, existingDocuments = [], onSuccess }) {
  const addDoc = useAddLegalDocument(caseNo);
  const [files, setFiles] = useState({});
  const [showHelper, setShowHelper] = useState(false);
  const [showFir, setShowFir] = useState(false);
  const [showLr, setShowLr] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [errors, setErrors] = useState([]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) { setFiles({}); setErrors([]); setDone(false); setProgress({ current: 0, total: 0 }); setShowHelper(false); setShowFir(false); setShowLr(false); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape" && !uploading) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, uploading, onClose]);

  const existingByKey = Object.fromEntries(
    existingDocuments.filter((d) => (d?.documentType || d?.type) && d?.fileUrl)
      .map((d) => [(d.documentType || d.type).toLowerCase(), d.fileUrl])
  );

  const setFile = useCallback((key, file) => setFiles((p) => ({ ...p, [key]: file })), []);

  const buildList = () => {
    const list = [];
    const push = (key) => { if (files[key]) list.push({ documentType: key, file: files[key] }); };
    DRIVER_DOC_FIELDS.forEach(({ key }) => push(key));
    DOCUMENT_FIELDS.forEach(({ key }) => push(key));
    if (showHelper) HELPER_DOC_FIELDS.forEach(({ key }) => push(key));
    if (showFir) push(FIR_DOC_FIELD.key);
    if (showLr) push(LR_DOC_FIELD.key);
    ACCIDENT_IMAGE_FIELDS.forEach(({ key }) => push(key));
    return list;
  };

  const count = buildList().length;

  const handleUpload = async () => {
    const list = buildList();
    if (!list.length) return;
    setUploading(true);
    setErrors([]);
    setProgress({ current: 0, total: list.length });
    const errs = [];
    for (let i = 0; i < list.length; i++) {
      setProgress({ current: i + 1, total: list.length });
      try {
        // useAddLegalDocument expects { type, file } — mapping documentType → type here
        await addDoc.mutateAsync({ type: list[i].documentType, file: list[i].file });
      } catch (err) {
        errs.push(`${list[i].documentType}: ${err?.message || "failed"}`);
      }
    }
    setUploading(false);
    setErrors(errs);
    if (!errs.length) {
      setDone(true);
      setTimeout(() => onSuccess?.(), 900);
    }
  };

  if (!open) return null;

  const pill = (label, state, set) => (
    <button
      key={label} onClick={() => set((p) => !p)} disabled={uploading}
      style={{
        fontSize: 11, fontWeight: 600, padding: "4px 11px", borderRadius: 20, cursor: "pointer",
        background: state ? "rgba(99,102,241,.12)" : "rgba(148,163,184,.07)",
        color: state ? "#a5b4fc" : "var(--wd-t2)",
        border: state ? "1px solid rgba(99,102,241,.25)" : "1px solid var(--wd-border)",
        transition: "all .12s",
      }}
    >
      {state ? "✓ " : ""}{label}
    </button>
  );

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !uploading) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(0,0,0,.65)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div style={{ width: "100%", maxWidth: 640, maxHeight: "90vh", display: "flex", flexDirection: "column", borderRadius: 16, background: "var(--wd-surface,#111827)", border: "1px solid rgba(99,102,241,.18)", boxShadow: "0 24px 72px rgba(0,0,0,.7)", overflow: "hidden" }}>
        {/* header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,.05)", flexShrink: 0 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: "rgba(99,102,241,.12)", border: "1px solid rgba(99,102,241,.25)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>📎</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--wd-t1)" }}>Upload Documents</div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>Case {caseNo} — choose files per section</div>
          </div>
          {!uploading && (
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "var(--wd-t3)", padding: 4, lineHeight: 1, borderRadius: 6 }}>✕</button>
          )}
        </div>

        {/* body */}
        <div style={{ overflowY: "auto", flex: 1, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 20 }}>
          {done ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "40px 0", textAlign: "center" }}>
              <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(34,197,94,.1)", border: "1px solid rgba(34,197,94,.25)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>✓</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--wd-t1)" }}>All documents uploaded</div>
              <div style={{ fontSize: 12, color: "var(--wd-t3)" }}>Closing…</div>
            </div>
          ) : (
            <>
              {/* optional section toggles */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", padding: "10px 12px", background: "rgba(255,255,255,.02)", border: "1px solid rgba(255,255,255,.05)", borderRadius: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--wd-t3)" }}>Optional sections</span>
                {pill("Helper docs", showHelper, setShowHelper)}
                {pill("FIR document", showFir, setShowFir)}
                {pill("LR document", showLr, setShowLr)}
              </div>

              {/* driver */}
              <div>
                <DocSectionHead icon="🪪" label="Driver Documents" count={DRIVER_DOC_FIELDS.length} />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 10 }}>
                  {DRIVER_DOC_FIELDS.map(({ key, label }) => (
                    <DocFieldRow key={key} label={label} docKey={key} files={files} onChange={setFile} existingUrl={existingByKey[key.toLowerCase()]} disabled={uploading} />
                  ))}
                </div>
              </div>

              {/* vehicle */}
              <div>
                <DocSectionHead icon="🚛" label="Vehicle Documents" count={DOCUMENT_FIELDS.length} />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 10 }}>
                  {DOCUMENT_FIELDS.map(({ key, label }) => (
                    <DocFieldRow key={key} label={label} docKey={key} files={files} onChange={setFile} existingUrl={existingByKey[key.toLowerCase()]} disabled={uploading} />
                  ))}
                </div>
              </div>

              {/* helper (conditional) */}
              {showHelper && (
                <div>
                  <DocSectionHead icon="👥" label="Co-Pilot / Helper Documents" count={HELPER_DOC_FIELDS.length} />
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 10 }}>
                    {HELPER_DOC_FIELDS.map(({ key, label }) => (
                      <DocFieldRow key={key} label={label} docKey={key} files={files} onChange={setFile} existingUrl={existingByKey[key.toLowerCase()]} disabled={uploading} />
                    ))}
                  </div>
                </div>
              )}

              {/* FIR (conditional) */}
              {showFir && (
                <div>
                  <DocSectionHead icon="🚨" label="FIR / GD Document" />
                  <div style={{ maxWidth: 200 }}>
                    <DocFieldRow label={FIR_DOC_FIELD.label} docKey={FIR_DOC_FIELD.key} files={files} onChange={setFile} existingUrl={existingByKey[FIR_DOC_FIELD.key.toLowerCase()]} disabled={uploading} />
                  </div>
                </div>
              )}

              {/* LR (conditional) */}
              {showLr && (
                <div>
                  <DocSectionHead icon="📦" label="LR / Loading Receipt" />
                  <div style={{ maxWidth: 200 }}>
                    <DocFieldRow label={LR_DOC_FIELD.label} docKey={LR_DOC_FIELD.key} files={files} onChange={setFile} existingUrl={existingByKey[LR_DOC_FIELD.key.toLowerCase()]} disabled={uploading} />
                  </div>
                </div>
              )}

              {/* accident images */}
              <div>
                <DocSectionHead icon="📸" label="Accident Images" count={ACCIDENT_IMAGE_FIELDS.length} />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 10 }}>
                  {ACCIDENT_IMAGE_FIELDS.map(({ key, label }) => (
                    <DocFieldRow key={key} label={label} docKey={key} files={files} onChange={setFile} existingUrl={existingByKey[key.toLowerCase()]} disabled={uploading} />
                  ))}
                </div>
              </div>

              {errors.length > 0 && (
                <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgba(239,68,68,.07)", border: "1px solid rgba(239,68,68,.2)" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--wd-red)", marginBottom: 6 }}>⚠️ Some uploads failed:</div>
                  {errors.map((e, i) => <div key={i} style={{ fontSize: 12, color: "#fca5a5", marginTop: 2 }}>{e}</div>)}
                </div>
              )}

              {uploading && (
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: 600, color: "#818cf8" }}>
                    <span>Uploading {progress.current} of {progress.total}…</span>
                    <span>{progress.total ? Math.round((progress.current / progress.total) * 100) : 0}%</span>
                  </div>
                  <div style={{ height: 4, borderRadius: 99, background: "rgba(99,102,241,.15)" }}>
                    <div style={{ height: "100%", borderRadius: 99, background: "var(--wd-indigo,#6366f1)", width: `${progress.total ? Math.round((progress.current / progress.total) * 100) : 0}%`, transition: "width .2s ease" }} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* footer */}
        {!done && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: "1px solid rgba(255,255,255,.05)", flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: count > 0 ? "#818cf8" : "var(--wd-t3)" }}>
              {count > 0 ? `📎 ${count} file${count !== 1 ? "s" : ""} ready to upload` : "No files selected yet"}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={onClose} disabled={uploading}>Cancel</button>
              <button
                className="wd-btn wd-btn-primary wd-btn-sm" onClick={handleUpload} disabled={uploading || count === 0}
                style={{ opacity: count === 0 ? 0.4 : 1, cursor: count === 0 ? "not-allowed" : "pointer" }}
              >
                {uploading ? "Uploading…" : `⬆ Upload${count > 0 ? ` (${count})` : ""}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function Badge({ variant, children }) {
  const colors = {
    green: { bg: "rgba(34,197,94,.12)", fg: "var(--wd-green)" },
    blue: { bg: "rgba(59,130,246,.12)", fg: "var(--wd-blue)" },
    yellow: { bg: "rgba(234,179,8,.12)", fg: "var(--wd-yellow, #eab308)" },
    red: { bg: "rgba(239,68,68,.12)", fg: "var(--wd-red)" },
    gray: { bg: "rgba(148,163,184,.12)", fg: "var(--wd-t3)" },
  }[variant] || { bg: "rgba(148,163,184,.12)", fg: "var(--wd-t3)" };
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20, background: colors.bg, color: colors.fg }}>
      {children}
    </span>
  );
}

//------------Legal Bail-process — WorkshopTab-style phase UI-----------------//

/* ══════════════════ Step card / header — same visual language as
   StepClaimForm / StepWorkshopEstimate in WorkshopIntakesteps.jsx ══════════════════ */

const STEP_STATE_STYLES = {
  active:  { color: "var(--wd-blue)",   bg: "rgba(59,130,246,.04)" },
  warning: { color: "var(--wd-orange)", bg: "rgba(249,115,22,.04)" },
  done:    { color: "var(--wd-green)",  bg: "rgba(34,197,94,.04)" },
  info:    { color: "var(--wd-purple, #a855f7)", bg: "rgba(168,85,247,.04)" },
};

export function StepCard({ state = "active", children }) {
  const s = STEP_STATE_STYLES[state] || STEP_STATE_STYLES.active;
  return (
    <div style={{
      borderRadius: 10, overflow: "hidden",
      border: `1px solid ${s.color}44`,
      borderLeft: `3px solid ${s.color}`,
      background: s.bg,
      padding: "14px 16px",
    }}>
      {children}
    </div>
  );
}

export function StepHeader({ icon, title, subtitle, badge, badgeColor = "var(--wd-blue)" }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-t1)" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>{subtitle}</div>}
        </div>
      </div>
      {badge && (
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20, flexShrink: 0,
          background: `${badgeColor}18`, color: badgeColor, border: `1px solid ${badgeColor}44`,
          textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap",
        }}>
          {badge}
        </span>
      )}
    </div>
  );
}

/* Drag-and-drop file zone — replaces the raw <input type="file"> + plain
   button. Same pill/drop-zone treatment as intake steps' PdfUploadZone. */
export function FileDropZone({ label, file, existingLabel, existingUrl, onFile, onClear, dragId }) {
  const [dragOver, setDragOver] = useState(false);
  const hasFile = file || existingUrl;

  if (hasFile) {
    return (
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "6px 12px", borderRadius: 8,
        background: "rgba(59,130,246,.07)", border: "1px solid rgba(59,130,246,.2)",
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--wd-blue)" strokeWidth="2" style={{ flexShrink: 0 }}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
        <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--wd-blue)" }}>{label}</span>
          <span style={{
            fontSize: 10, color: "var(--wd-t3)", fontFamily: "var(--wd-mono)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180,
          }}>
            {file ? file.name : existingLabel || "Uploaded"}
          </span>
        </div>
        {existingUrl && !file && (
          <a href={existingUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
            style={{
              fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 6,
              background: "rgba(59,130,246,.15)", color: "var(--wd-blue)",
              border: "1px solid rgba(59,130,246,.25)", textDecoration: "none", whiteSpace: "nowrap",
            }}>
            View ↗
          </a>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onClear(); }}
          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--wd-t3)", fontSize: 14, padding: 0, lineHeight: 1 }}
          title="Remove"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
      onClick={() => document.getElementById(dragId)?.click()}
      style={{
        border: `1.5px dashed ${dragOver ? "var(--wd-blue)" : "var(--wd-border-md)"}`,
        borderRadius: 8, padding: "10px 12px",
        background: dragOver ? "rgba(59,130,246,.05)" : "var(--wd-hover)",
        cursor: "pointer", transition: "all .15s",
        display: "flex", alignItems: "center", gap: 8,
      }}
    >
      <input id={dragId} type="file" style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files[0]; if (f) onFile(f); }} />
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--wd-t3)" strokeWidth="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
      </svg>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--wd-t2)" }}>Drop file or click to browse</div>
        <div style={{ fontSize: 10, color: "var(--wd-t3)" }}>{label}</div>
      </div>
    </div>
  );
}

export function CompletedRow({ label, children, initialOpen = false }) {
  const [open, setOpen] = useState(initialOpen);
  return (
    <div style={{ borderRadius: 8, border: "1px solid rgba(34,197,94,.2)", background: "rgba(34,197,94,.04)", overflow: "hidden" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{ width: "100%", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", textAlign: "left" }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--wd-green)" strokeWidth="2.5">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-t1)", flex: 1 }}>{label}</span>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20,
          background: "rgba(34,197,94,.12)", color: "var(--wd-green)",
          border: "1px solid rgba(34,197,94,.25)", textTransform: "uppercase", letterSpacing: ".04em", marginRight: 4,
        }}>Done</span>
        <span style={{ fontSize: 11, color: "var(--wd-t3)", display: "inline-block", transition: "transform .15s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
      </button>
      {open && <div style={{ borderTop: "1px solid rgba(34,197,94,.15)", padding: 12 }}>{children}</div>}
    </div>
  );
}

export function SLabel({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--wd-t3)", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
      {children}
      <span style={{ flex: 1, height: 1, background: "var(--wd-border)" }} />
    </div>
  );
}

export function UpNextList({ items }) {
  if (!items.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {items.map((label, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 7, background: "var(--wd-hover)", border: "1px solid var(--wd-border)" }}>
          <span style={{ fontSize: 12 }}>🔒</span>
          <span style={{ fontSize: 11, color: "var(--wd-t3)" }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

// Slices a flat step array into Completed / Action required / Up next —
// same shape WorkshopTab's PhaseContent uses for driver/claim/repair/delivery.
export function BailPhaseContent({ steps }) {
  const activeIdx = steps.findIndex((s) => !s.done);
  const completed = activeIdx === -1 ? steps : steps.slice(0, activeIdx);
  const active = activeIdx === -1 ? null : steps[activeIdx];
  const upcoming = activeIdx === -1 ? [] : steps.slice(activeIdx + 1);
  const allDone = activeIdx === -1;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {completed.length > 0 && (
        <>
          <SLabel>Completed</SLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {completed.map((s) => (
              <CompletedRow key={s.key} label={`${s.stepNo}. ${s.label}`}>{s.component}</CompletedRow>
            ))}
          </div>
        </>
      )}

      {active && (
        <>
          <SLabel>Action required</SLabel>
          {active.component}
        </>
      )}

      {allDone && (
        <div style={{ padding: "14px 16px", borderRadius: 10, textAlign: "center", background: "rgba(34,197,94,.06)", border: "1px solid rgba(34,197,94,.2)" }}>
          <div style={{ fontSize: 20, marginBottom: 4 }}>🎉</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-green)" }}>All steps complete</div>
        </div>
      )}

      {upcoming.length > 0 && (
        <>
          <SLabel>Up next</SLabel>
          <UpNextList items={upcoming.map((s) => `${s.stepNo}. ${s.label}`)} />
        </>
      )}
    </div>
  );
}

// Clickable phase tab bar — checkmark + green once every step in that
// phase is done, yellow dot while it's the active phase. Same visuals as
// WorkshopTab's phase tabs (intake / claim & survey / repair / delivery).
export function PhaseTabBar({ phases, selected, onSelect, isDone, isActive }) {
  return (
    <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: "1px solid var(--wd-border)" }}>
      {phases.map((p, i) => {
        const done = isDone(p.key);
        const active = isActive(p.key);
        const isSelected = selected === p.key;
        return (
          <button
            key={p.key}
            onClick={() => onSelect(p.key)}
            style={{
              flex: 1, textAlign: "center", padding: "8px 6px",
              fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
              cursor: "pointer", border: "none",
              borderLeft: i > 0 ? "1px solid var(--wd-border)" : "none",
              borderBottom: isSelected ? "2px solid var(--wd-blue)" : "2px solid transparent",
              background: isSelected ? "rgba(59,130,246,.09)" : done ? "rgba(34,197,94,.05)" : active ? "rgba(234,179,8,.05)" : "transparent",
              color: isSelected ? "var(--wd-blue)" : done ? "var(--wd-green)" : active ? "var(--wd-yellow)" : "var(--wd-t3)",
              transition: "background .12s, color .12s",
            }}
          >
            {done && !isSelected && <span style={{ marginRight: 3 }}>✓</span>}
            {active && !isSelected && (
              <span style={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", background: "var(--wd-yellow)", marginRight: 5, verticalAlign: "middle" }} />
            )}
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

 export function SectionCard({ title, action, children }) {
  return (
    <div className="wd-card" style={{ padding: 18 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-t1)" }}>
          {title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export function InfoRow({ label, value, mono }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <FieldLabel>{label}</FieldLabel>
      <span
        style={{
          fontSize: 13,
          color: "var(--wd-t1)",
          fontFamily: mono ? "var(--wd-mono)" : "var(--wd-sans)",
        }}
      >
        {value || "—"}
      </span>
    </div>
  );
}

export function FieldLabel({ children }) {
  return (
    <span
      style={{
        fontSize: 11,
        color: "var(--wd-t3)",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: ".05em",
      }}
    >
      {children}
    </span>
  );
}

export function PriorityBadge({ priority }) {
  const colors = {
    LOW: "var(--wd-green)",
    MEDIUM: "var(--wd-yellow, #eab308)",
    HIGH: "var(--wd-orange)",
    CRITICAL: "var(--wd-red)",
  };
  const c = colors[priority] || "var(--wd-t3)";
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 9px",
        borderRadius: 20,
        background: `${c}18`,
        color: c,
        border: `1px solid ${c}33`,
      }}
    >
      {priority}
    </span>
  );
}

export function FlagBadge({ color, icon, label }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 9px",
        borderRadius: 20,
        background: `${color}18`,
        color,
        border: `1px solid ${color}33`,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      {icon} {label}
    </span>
  );
}